import { getWebSocketUrl } from "src/config";
import { apiClient } from "./apiClient";

export interface LicenseTermsVersion {
  id: string;
  version: string;
  title: string;
  documentUrl: string;
  termsHash: string;
  publishedAt: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** Snapshot of a single terms acceptance owned by the authenticated user. */
export interface LicenseTermsAcceptanceRecord {
  id: string;
  licenseId: string;
  termsVersionId: string;
  organisationId?: string | null;
  userId: string;
  userEmail: string;
  acceptedAt: string;
  termsVersion: string;
  termsDocumentUrl: string;
  termsHash: string;
  ipAddress?: string | null;
  userAgent?: string | null;
  acceptanceMethod: "WEB" | "ADMIN_MANUAL" | "EMAIL_REPLY" | "IMPORT" | string;
  note?: string | null;
  termsVersionRef?: Pick<
    LicenseTermsVersion,
    | "id"
    | "version"
    | "title"
    | "documentUrl"
    | "termsHash"
    | "publishedAt"
    | "isActive"
  > | null;
}

export interface LicenseTermsAcceptanceState {
  requiresAcceptance: boolean;
  hasAccepted: boolean;
  license: { id: string; key: string; name: string } | null;
  termsVersion: LicenseTermsVersion | null;
  acceptance: LicenseTermsAcceptanceRecord | null;
  /** Full history for the current user (newest first), shown on the licensed dashboard. */
  acceptances?: LicenseTermsAcceptanceRecord[];
  blockingReason?: string;
}

export interface CreateLicenseTermsVersionData {
  version: string;
  title: string;
  documentUrl: string;
  termsHash: string;
  publishedAt?: string;
  isActive?: boolean;
}

export interface ManualLicenseTermsAcceptanceData {
  licenseId: string;
  userId: string;
  termsVersionId?: string;
  note?: string;
}

export interface LicenseTermsDocumentUpload {
  fileName: string;
  originalName: string;
  documentUrl: string;
  termsHash: string;
  fileSize: number;
  mimeType?: string;
}

const apiUrl = getWebSocketUrl();

export const resolveLicenseTermsDocumentUrl = (documentUrl: string): string => {
  if (!documentUrl) return "";
  if (/^(https?:)?\/\//.test(documentUrl) || documentUrl.startsWith("data:")) {
    return documentUrl;
  }
  return `${apiUrl}${documentUrl.startsWith("/") ? "" : "/"}${documentUrl}`;
};

export const LicenseTermsService = {
  async fetchCurrent(_token?: string): Promise<LicenseTermsAcceptanceState> {
    return apiClient.get<LicenseTermsAcceptanceState>("/license-terms/current");
  },

  async acceptCurrent(_token?: string): Promise<LicenseTermsAcceptanceState> {
    const payload = await apiClient.post<{
      state: LicenseTermsAcceptanceState;
    }>("/license-terms/accept");
    return payload.state;
  },

  async listVersions(_token?: string): Promise<LicenseTermsVersion[]> {
    return apiClient.get<LicenseTermsVersion[]>(
      "/license-terms/admin/versions",
    );
  },

  async createVersion(
    _token: string,
    data: CreateLicenseTermsVersionData,
  ): Promise<LicenseTermsVersion> {
    return apiClient.post<LicenseTermsVersion>(
      "/license-terms/admin/versions",
      data,
    );
  },

  async uploadDocument(
    _token: string,
    file: File,
  ): Promise<LicenseTermsDocumentUpload> {
    const body = new FormData();
    body.append("file", file);
    return apiClient.post<LicenseTermsDocumentUpload>(
      "/license-terms/admin/documents",
      body,
    );
  },

  async activateVersion(
    _token: string,
    id: string,
  ): Promise<LicenseTermsVersion> {
    return apiClient.patch<LicenseTermsVersion>(
      `/license-terms/admin/versions/${id}/activate`,
    );
  },

  async listAcceptances(_token?: string): Promise<unknown[]> {
    return apiClient.get<unknown[]>("/license-terms/admin/acceptances");
  },

  async createManualAcceptance(
    _token: string,
    data: ManualLicenseTermsAcceptanceData,
  ): Promise<unknown> {
    return apiClient.post<unknown>(
      "/license-terms/admin/acceptances/manual",
      data,
    );
  },
};

export default LicenseTermsService;
